// rename-select-alias planner. Two paths:
//   - The select-item already has an alias (explicit `AS x` or implicit
//     trailing `x`) — find the alias byte range and replace it with newAlias.
//   - The select-item has no alias — append ` AS newAlias` after the
//     expression's last non-whitespace byte (= range.endOffset since the
//     extractor trims item ranges).
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Structural Edit Applicator — modify.

import type { PlannedByteEdit, SelectItem, StructuralEdit } from '../types';

import type { EditCtx } from './applyStructuralEdit';
import { insertEdit } from './applyStructuralEdit.shared';
import { rangeFromOffsets } from '../../sql-grammar/document/lineIndex';

export function planRenameAliasEdit(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'rename-select-alias' }>,
): readonly PlannedByteEdit[] {
  const items = ctx.structure.selectItems ?? [];
  if (edit.index < 0 || edit.index >= items.length) return [];
  const item = items[edit.index];
  if (item.alias === undefined) {
    return [insertEdit(ctx, item.range.endOffset, ` AS ${edit.newAlias}`, 'rename-select-alias:add')];
  }
  const aliasRange = findAliasByteRange(ctx.source, item);
  if (!aliasRange) return [];
  return [{
    range: rangeFromOffsets(ctx.lineIdx, aliasRange.start, aliasRange.end),
    replacement: edit.newAlias,
    rationale: 'rename-select-alias:replace',
  }];
}

function findAliasByteRange(
  source: string,
  item: SelectItem,
): { start: number; end: number } | null {
  const lo = item.range.startOffset;
  let end = item.range.endOffset;
  while (end > lo && /\s/.test(source[end - 1])) end--;
  if (end <= lo) return null;
  const last = source[end - 1];
  if (last === '`' || last === ']' || last === '"') {
    const open = last === '`' ? '`' : last === ']' ? '[' : '"';
    let j = end - 2;
    while (j >= lo && source[j] !== open) j--;
    if (j < lo) return null;
    return { start: j, end };
  }
  if (!/[A-Za-z0-9_$]/.test(last)) return null;
  let start = end;
  while (start > lo && /[A-Za-z0-9_$]/.test(source[start - 1])) start--;
  if (!/[A-Za-z_]/.test(source[start])) return null;
  return { start, end };
}
